import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

function SveaFinancingCard({ t }) {
  const { currentOrganization, fetchUser } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  // Credential form state
  const [showCredForm, setShowCredForm] = useState(false);
  const [sellerId, setSellerId] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [keyVersion, setKeyVersion] = useState("1");
  const [saving, setSaving] = useState(false);

  // Contact request state
  const [contactChecked, setContactChecked] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (organizationId) fetchOrgData();
  }, [organizationId]);

  const fetchOrgData = async () => {
    try {
      const response = await axios.get(`/api/organizations/${organizationId}/`);
      setOrg(response.data);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const isConfigured = currentOrganization?.svea_financing_enabled;
  const contactRequestedAt = org?.svea_contact_requested_at;

  const handleSaveCredentials = async () => {
    setSaving(true);
    try {
      await axios.post(`/api/organizations/${organizationId}/svea/configure/`, {
        seller_id: sellerId,
        secret_key: secretKey,
        key_version: parseInt(keyVersion, 10) || 1,
      });
      setSnackbar({ open: true, message: t("svea.configSaved"), severity: "success" });
      setShowCredForm(false);
      fetchOrgData();
      fetchUser();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t("svea.configSaveFailed"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await axios.post(`/api/organizations/${organizationId}/svea/deactivate/`);
      setSnackbar({ open: true, message: t("svea.deactivated"), severity: "success" });
      fetchOrgData();
      fetchUser();
    } catch (err) {
      setSnackbar({ open: true, message: t("Error"), severity: "error" });
    }
  };

  const handleSendContactRequest = async () => {
    setSendingContact(true);
    try {
      await axios.post(`/api/organizations/${organizationId}/svea/request-contact/`);
      setSnackbar({ open: true, message: t("svea.contactRequestSent"), severity: "success" });
      fetchOrgData();
    } catch (err) {
      if (err.response?.status === 409) {
        setSnackbar({ open: true, message: t("svea.contactRequestSent"), severity: "info" });
      } else {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || t("Error"),
          severity: "error",
        });
      }
    } finally {
      setSendingContact(false);
    }
  };

  if (loading) return null;

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "#eff6ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 2,
                flexShrink: 0,
              }}
            >
              <AccountBalanceIcon sx={{ color: "#1e40af", fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t("svea.settingsTitle")}
                </Typography>
                {isConfigured && (
                  <Chip
                    label={t("svea.connected")}
                    size="small"
                    color="success"
                    icon={<CheckCircleIcon />}
                    sx={{ height: 22, fontSize: "11px" }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("svea.settingsDescription")}
              </Typography>
            </Box>
          </Box>

          {/* State C: Configured */}
          {isConfigured && (
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" color="error" size="small" onClick={handleDeactivate}>
                {t("svea.deactivate")}
              </Button>
            </Box>
          )}

          {/* State A/B: Not configured */}
          {!isConfigured && (
            <>
              <Alert severity="info" sx={{ mb: 2, fontSize: "13px" }}>
                {t("svea.requiresAgreement")}
              </Alert>

              {/* Contact request sent */}
              {contactRequestedAt && (
                <Alert severity="success" sx={{ mb: 2, fontSize: "13px" }}>
                  {t("svea.contactRequestSentAt")} {new Date(contactRequestedAt).toLocaleDateString("fi-FI")}. {t("svea.sveaWillContact")}
                </Alert>
              )}

              {/* Credential form toggle */}
              <Button
                variant={showCredForm ? "contained" : "outlined"}
                size="small"
                onClick={() => setShowCredForm(!showCredForm)}
                sx={{ mb: 1 }}
              >
                {t("svea.haveCredentials")}
              </Button>

              <Collapse in={showCredForm}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5, mb: 2 }}>
                  <TextField
                    label={t("svea.sellerId")}
                    value={sellerId}
                    onChange={(e) => setSellerId(e.target.value)}
                    helperText={t("svea.sellerIdHelp")}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label={t("svea.secretKey")}
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    helperText={t("svea.secretKeyHelp")}
                    type="password"
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label={t("svea.keyVersion")}
                    value={keyVersion}
                    onChange={(e) => setKeyVersion(e.target.value)}
                    type="number"
                    size="small"
                    sx={{ maxWidth: 120 }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveCredentials}
                      disabled={saving || !sellerId || !secretKey}
                      startIcon={saving ? <CircularProgress size={14} /> : null}
                    >
                      {t("Save")}
                    </Button>
                    <Button size="small" onClick={() => setShowCredForm(false)}>
                      {t("Cancel")}
                    </Button>
                  </Box>
                </Box>
              </Collapse>

              {/* Contact request section */}
              {!contactRequestedAt && !showCredForm && (
                <Box sx={{ mt: 2, borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={contactChecked}
                        onChange={(e) => setContactChecked(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">{t("svea.contactCheckbox")}</Typography>
                    }
                  />
                  <Collapse in={contactChecked}>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      startIcon={sendingContact ? <CircularProgress size={14} /> : <SendIcon />}
                      onClick={handleSendContactRequest}
                      disabled={sendingContact}
                      sx={{ mt: 1 }}
                    >
                      {t("svea.sendContactRequest")}
                    </Button>
                  </Collapse>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default withTranslation()(SveaFinancingCard);
